import React, { FC, PropsWithChildren } from 'react';

import { Box, Paper, SxProps, TableContainer, Theme } from '@mui/material';
import Scrollbars from 'react-custom-scrollbars-2';

import useScrollPercentage from 'shared/hooks/useScrollPercentage';
import { TablePagination } from 'shared/ui/pagination';

import CircularProgressCenter from '../CircularProgressCenter';
import { CustomScrollbar } from '../custom-scrollbar';
import { TableContentWrapper } from '../table-content-wrapper';

interface IProps extends PropsWithChildren {
  columns: any;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  total: number;
  sx?: SxProps<Theme>;
  isDataLoading?: boolean;
}

export const TableLayout: FC<IProps> = ({
  columns,
  children,
  pageSize,
  setPageSize,
  total,
  sx,
  isDataLoading
}) => {
  const { handleScroll, scrollbarRef, contentRef, isShowScroll, ...customScrollProps } =
    useScrollPercentage();

  return (
    <>
      {isDataLoading ? (
        <CircularProgressCenter top='50%' />
      ) : (
        <>
          <Box
            sx={{
              gridRow: '3',
              flex: '1 1',
              overflow: 'auto',
              '&.MuiBox-root': {
                maxWidth: '100%',
                width: '100%'
              }
            }}>
            <TableContainer
              sx={{
                height: 1,
                boxShadow: 'none',
                position: 'relative',
                display: 'flex',
                justifyContent: 'space-between',
                overflow: 'hidden',
                backgroundImage: 'none',
                '::-webkit-scrollbar': {
                  display: 'none',
                  width: '0',
                  background: 'transparent',
                  scrollbarColor: 'transparent transparent' //paletteScroll
                },
                ...sx
              }}
              component={Paper}>
              <Scrollbars
                ref={scrollbarRef}
                style={{
                  width: '100%',
                  borderRadius: '16px',
                  height: 'calc(100% - 12px)',
                  background: 'var(--mui-palette-background-paper)',
                  scrollbarColor: 'transparent transparent' //paletteScroll
                }}
                id=''
                renderTrackVertical={undefined}
                renderThumbVertical={undefined}
                onScroll={handleScroll}>
                <TableContentWrapper
                  contentRef={contentRef}
                  columns={columns}
                  sx={{
                    minWidth: 0,
                    borderBottom: 'none',
                    position: 'relative',
                    '::-webkit-scrollbar': {
                      display: 'none',
                      width: '0',
                      background: 'transparent' //paletteScroll
                    },
                    '.MuiCircularProgress-root': {
                      width: '52px',
                      height: '52px'
                    }
                  }}>
                  {children}
                </TableContentWrapper>
              </Scrollbars>
              {isShowScroll && <CustomScrollbar customScrollProps={customScrollProps} />}
            </TableContainer>
          </Box>

          <TablePagination pageSize={pageSize} setPageSize={setPageSize} total={total} />
        </>
      )}
    </>
  );
};
